import { get, post } from './services/request';
import { isSuccess } from './services/responseUtils';

export function login(username, password, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/login', {
        username,
        password,
        requestId,
        notificationToken,
    });
}

export function getUserSessionContext() {
    return get('/ui/user/session-context');
}

export function getLanguages() {
    return get('/ui/i18n/languages');
}

export function getLocales(language) {
    return get(`/ui/i18n/locales?language=${language}`);
}

export function getTimezones(language) {
    return get(`/ui/i18n/timezones?language=${language}`);
}

export async function getUserSessionContextGracefully() {
    let responseData = {};

    try {
        const response = await getUserSessionContext();
        if (isSuccess(response)) {
            responseData = response.data;
        } else {
            responseData = response.errors;
        }
    } catch (ex) {
        // ignore, use the default value
    }

    return responseData;
}

export function requestResetPassword(email, frcToken, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/password/request-reset', {
        email,
        'frc-captcha-response': frcToken,
        requestId,
        notificationToken,
    });
}

export function updateUsername(email, password, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/username/update', {
        email,
        password,
        requestId,
        notificationToken,
    });
}

/**
 * @typedef RequestResetBodyParams
 * @param {string} email users username
 * @param {string} frcToken token for Friendly Captcha
 * @param {object} requestState contains requestId and notificationToken for tracking requests
 * @param {boolean} sendIncrementedEmail if true, send plus one email template
 */

/**
 * VeevaID Self Registration
 * @param {RequestResetBodyParams}
 */
export function requestSelfRegistration({ email, frcToken, requestState, sendIncrementedEmail }) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/self-registration/request-registration', {
        email,
        'frc-captcha-response': frcToken,
        requestId,
        notificationToken,
        sendIncrementedEmail,
    });
}

/**
 * Non-Vault App Self Registration
 * @param {object} RequestResetBodyParams
 */
export function requestNonVaultAppRegistration({
    email,
    frcToken,
    requestState,
    sendIncrementedEmail,
}) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/non-vaultapp-registration/request-registration', {
        email,
        'frc-captcha-response': frcToken,
        requestId,
        notificationToken,
        sendIncrementedEmail,
    });
}

/**
 * @typedef RegisterResponse
 * @property {string} responseStatus
 * @property {object} data
 * @property {"VAULT_APP"|"VIMC"|"VEEVAID"|"NON_VAULT_APP"} data.registrationType registration flow
 *     type
 * @property {string} data.targetUrl endpoint URL to call or URL to redirect to
 * @property {string} [data.appRegToken] token to pass to post-registration endpoint (targetUrl),
 *     if applicable
 */
/**
 * Register VeevaID user
 * @param {object} user
 * @param {object} requestState
 * @returns {Promise.<RegisterResponse>}
 */
export function register(user, requestState) {
    const {
        firstName,
        lastName,
        prefix,
        middleName,
        suffix,
        password,
        language,
        locale,
        timezone,
    } = user;
    const { requestId, notificationToken } = requestState;
    return post('/ui/registration', {
        firstName,
        lastName,
        prefix,
        middleName,
        suffix,
        password,
        language,
        locale,
        timezone,
        requestId,
        notificationToken,
    });
}

export function getApplicationMetaData(requestState = {}) {
    const { requestId, notificationToken } = requestState;
    return get('/ui/application', {
        requestId,
        notificationToken,
    });
}

export function getFriendlyCaptchaSiteKey() {
    return get('/ui/captcha-sitekey/friendly');
}

/**
 * Resets password from LoginPage
 * @param {string} newPassword
 * @param {object} requestState
 */
export function updatePassword(newPassword, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/password/update', {
        newPassword,
        requestId,
        notificationToken,
    });
}

export function loginWithVerificationCode(code, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/verification-code/login', { code, notificationToken, requestId });
}

export function updateUsernameWithVerificationCode(code, requestState) {
    const { requestId, notificationToken } = requestState;
    return post('/ui/verification-code/username-update', { code, notificationToken, requestId });
}

export function resendVerificationCode() {
    return post('/ui/verification-code/resend');
}

/**
 * @typedef ApplicationsResponse
 * @prop {string} name
 * @prop {string} companyName
 * @prop {string} companyIdentifier
 * @prop {string} description
 * @prop {string} [lastLogin] ISO datetime
 * @prop {string} lastLoginFormatted human-readable date, formatted by locale
 * @prop {string} appRedirectUrl
 */
/**
 * @returns {Promise.<ApplicationsResponse>}
 */
export function fetchHomeData() {
    return get('/ui/user/home');
}

/**
 * @typedef Study
 * @prop {string} studyName
 * @prop {string} redirectUrl
 */
/**
 * @typedef StudiesResponse
 * @prop {string} companyName
 * @prop {string} redirectUrl
 * @prop {string} [lastLogin] ISO datetime
 * @prop {string} lastLoginFormatted human-readable date, formatted by locale
 * @prop {Study[]} studies
 */
/**
 * @returns {Promise.<StudiesResponse>}
 */
export function fetchStudiesData() {
    return get('/ui/user/studies');
}

export function fetchPendingRegistrations() {
    return get('/ui/post_registration');
}

export function fetchRecentActivityData() {
    return get('/ui/user/recent-activity');
}
